import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  Tooltip
} from '@mui/material'
import { SerializedError } from '@reduxjs/toolkit'
import dayjs, { Dayjs } from 'dayjs'
import {
  FC,
  FocusEventHandler,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NotificationContext } from '../../../providers'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { useUpdateCustomerDetailMutation } from '../../../redux/api'
import {
  useSendFeedbackEmailMutation,
  useSendFeedbackLinkMutation
} from '../../../redux/api/feedbackApi'
import SdkError from '../../../utils/handleError/SdkError'
import { DelayedSendPicker } from '../../SharedUI/DelayedSendPicker'

interface Props {
  cancelCallback: () => void
  postSubmitCallback: () => void // call after successful submission
}

const SendFeedbackLinkForm: FC<Props> = ({
  cancelCallback,
  postSubmitCallback
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const { customer, repairOrder } = useContext(RepairOrderContext)
  const { setNotice, setError } = useContext(NotificationContext)

  const [sendSmsFeedbackLink] = useSendFeedbackLinkMutation()
  const [sendEmailFeedbackLink] = useSendFeedbackEmailMutation()
  const [updateCustomer] = useUpdateCustomerDetailMutation()

  const [sendSms, setSendSms] = useState<boolean>(true)
  const [smsTimed, setSmsTimed] = useState<boolean>(false)
  const [smsDate, setSmsDate] = useState<Dayjs>(dayjs().add(16, 'minutes'))
  const [smsDateError, setSmsDateError] = useState<boolean>(false)
  const [phone, setPhone] = useState<string>(customer!.information!.phone || '')
  const [phoneValidationError, setPhoneValidationError] =
    useState<boolean>(false)
  const [sendEmail, setSendEmail] = useState<boolean>(true)
  const [emailTimed, setEmailTimed] = useState<boolean>(false)
  const [emailDate, setEmailDate] = useState<Dayjs>(dayjs().add(1, 'minutes'))
  const [emailDateError, setEmailDateError] = useState<boolean>(false)
  const [email, setEmail] = useState<string>(customer!.information!.email || '')
  const [emailValidationError, setEmailValidationError] =
    useState<boolean>(false)
  const [editingContact, setEditingContact] = useState<boolean>(false)

  useEffect(() => {
    setPhone(customer!.information!.phone || '')
  }, [customer])

  useEffect(() => {
    setEmail(customer!.information!.email || '')
  }, [customer])

  const handleFocusContactField: FocusEventHandler<HTMLInputElement> = () => {
    setEditingContact(true)
  }

  const handleBlurEmailField: FocusEventHandler<HTMLInputElement> = async (
    event
  ) => {
    const newEmail = event.target.value
    const result = await updateCustomer({
      authorization: accessToken,
      customerEntry: {
        ...customer!,
        information: {
          ...customer!.information!,
          email: newEmail
        }
      }
    })
    if ('error' in result) {
      setError('Error updating email')
    } else {
      setNotice('Email updated')
    }
    setEditingContact(false)
  }

  const handleBlurPhoneField: FocusEventHandler<HTMLInputElement> = async (
    event
  ) => {
    const newPhone = event.target.value
    const result = await updateCustomer({
      authorization: accessToken,
      customerEntry: {
        ...customer!,
        information: {
          ...customer!.information!,
          phone: newPhone
        }
      }
    })
    if ('error' in result) {
      setError('Error updating phone number')
    } else {
      setNotice('Phone number updated')
    }
    setEditingContact(false)
  }

  const checkPhoneIsValid: () => boolean = useCallback(() => {
    const phonePattern = /^\d{3}-?\d{3}-?\d{4}$/
    const isMatch = phonePattern.test(phone)
    if (!isMatch) {
      setPhoneValidationError(true)
    }
    return isMatch
  }, [phone])

  const checkEmailIsValid: () => boolean = useCallback(() => {
    const emailPattern =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    const isMatch = emailPattern.test(email)
    if (!isMatch) {
      setEmailValidationError(true)
    }
    return isMatch
  }, [email])

  const handleSend: () => Promise<void> = async () => {
    if (smsDateError || emailDateError) {
      return
    }
    const promises: Promise<
      { data: string } | { error: SdkError | SerializedError }
    >[] = []
    if (sendSms) {
      if (!checkPhoneIsValid()) {
        return
      }
      const smsOutcome = sendSmsFeedbackLink({
        authorization: accessToken,
        sendFeedbackLinkRequest: {
          repairOrderId: repairOrder!.entry!.id,
          dateTime: smsTimed && smsDate ? smsDate.toDate() : undefined
        }
      })
      promises.push(smsOutcome)
    }
    if (sendEmail) {
      if (!checkEmailIsValid()) {
        return
      }
      const emailOutcome = sendEmailFeedbackLink({
        authorization: accessToken,
        sendFeedbackLinkRequest: {
          repairOrderId: repairOrder!.entry!.id,
          dateTime: emailTimed && emailDate ? emailDate.toDate() : undefined
        }
      })
      promises.push(emailOutcome)
    }

    if (promises.length > 0) {
      const outcomes = await Promise.all(promises)
      if (!outcomes.some((outcome) => 'data' in outcome)) {
        return // stop closing of form if all selected feedback messaging methods fail
      }
    }

    setNotice('Link sent successfully')

    postSubmitCallback()
  }

  return (
    <>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendSms}
                    onChange={(event) => setSendSms(event.target.checked)}
                  />
                }
                label="Send Feedback Link via SMS"
              />
            </FormGroup>
            {sendSms && (
              <Stack spacing={1}>
                <TextField
                  sx={{ width: '10rem' }}
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  label="Phone"
                  onBlur={handleBlurPhoneField}
                  onFocus={handleFocusContactField}
                />
                {phoneValidationError && (
                  <Alert color="error">
                    Phone number must be in the format 123-456-7890 or
                    1234567890.
                  </Alert>
                )}
                <DelayedSendPicker
                  useDelay={smsTimed}
                  setUseDelay={setSmsTimed}
                  delayedDateTime={smsDate}
                  setDelayedDateTime={setSmsDate}
                  setError={setSmsDateError}
                  minDateTime={dayjs().add(15, 'minutes')}
                  maxDateTime={dayjs().add(1, 'week')}
                  label=""
                  helperText="Time must be between 15 minutes and 1 week"
                />
              </Stack>
            )}
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmail}
                    onChange={(event) => setSendEmail(event.target.checked)}
                  />
                }
                label="Send Feedback Link via Email"
              />
            </FormGroup>
            {sendEmail && (
              <Stack spacing={1}>
                <TextField
                  sx={{ width: '20rem' }}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  label="Email"
                  onBlur={handleBlurEmailField}
                  onFocus={handleFocusContactField}
                />
                {emailValidationError && (
                  <Alert color="error">
                    Please enter a valid email address (e.g.
                    info@intuneauto.com).
                  </Alert>
                )}
                <DelayedSendPicker
                  useDelay={emailTimed}
                  setUseDelay={setEmailTimed}
                  delayedDateTime={emailDate}
                  setDelayedDateTime={setEmailDate}
                  setError={setEmailDateError}
                  minDateTime={dayjs()}
                  maxDateTime={dayjs().add(3, 'day')}
                  label=""
                  helperText="No more than 3 days in the future"
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          sx={{ color: 'gray.400' }}
          onClick={cancelCallback}>
          Back
        </Button>
        <Tooltip
          title={
            editingContact &&
            'This button is disabled until you have finished editing contact info'
          }>
          <span>
            <Button
              variant="contained"
              disabled={editingContact || (!sendSms && !sendEmail)}
              onClick={handleSend}>
              Send
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </>
  )
}

export default SendFeedbackLinkForm
